import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from '../assets/img/logo.jpg';
import { useAuthContext } from "../context/AuthProvider";
import "./panel.css";

/** NavLink de v6: el estado activo llega como argumento de className. */
const menuClass = ({ isActive }) => `menu-item${isActive ? ' active' : ''}`;

/**
 * Marco común de las tres áreas privadas.
 *
 * Administración, panel de la modelo y transmisiones eran tres archivos con el
 * mismo esqueleto copiado —barra lateral, cajón móvil, pie con la cuenta, barra
 * superior pegajosa— y con las diferencias mezcladas dentro. Al tocar uno, los
 * otros dos se quedaban atrás: el cierre del cajón al navegar, por ejemplo,
 * hubo que arreglarlo tres veces.
 *
 * Aquí queda el marco; cada área aporta solo lo suyo:
 *
 *   sections  [{ title, items: [{ to, icon, label, end }] }]
 *   title     rótulo de la barra superior para la ruta actual
 *   actions   lo que va a la derecha de la barra (saldo, "ver sitio"...)
 */
export default function PanelShell({ sections, title, actions }) {
    const { user, logout } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    // En móvil la barra lateral se superpone al contenido: al pulsar un enlace
    // se quedaba abierta tapando la pantalla a la que acababas de entrar.
    useEffect(() => setSidebarOpen(false), [location.pathname]);

    const exitUser = () => {
        logout();
        navigate("/");
    };

    const toggleSidebar = () => setSidebarOpen((open) => !open);

    // Una sección sin entradas visibles no debe dejar su rótulo suelto: es lo
    // que pasaba con "Administración", que encabezaba una sección vacía.
    const visibles = sections.filter((section) => section.items.some(Boolean));

    return (
        <div className={`cpanel-wrapper ${isSidebarOpen ? 'sidebar-open' : ''}`}>
            <aside className="cpanel-sidebar">
                <div className="sidebar-header">
                    <NavLink to="/" title="Volver al sitio público">
                        <img src={logo} alt="Afrodita Models" />
                    </NavLink>
                </div>

                <nav className="sidebar-menu" aria-label="Menú del panel">
                    {visibles.map((section) => (
                        <div key={section.title}>
                            <div className="menu-title">{section.title}</div>
                            {section.items.filter(Boolean).map((item) => (
                                <NavLink key={item.to} to={item.to} end={item.end} className={menuClass}>
                                    <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">{user?.name?.[0]?.toUpperCase() || 'U'}</div>
                        <div className="user-info">
                            <span className="user-name">{user?.name || 'Usuario'}</span>
                            <span className="user-role">{user?.roles?.[0] || 'Usuario'}</span>
                        </div>
                    </div>
                    <button className="logout-btn" onClick={exitUser}>
                        <i className="fa-solid fa-right-from-bracket" aria-hidden="true"></i>
                        <span>Cerrar sesión</span>
                    </button>
                </div>
            </aside>

            <main className="cpanel-main">
                <header className="cpanel-topbar">
                    <div className="topbar-start">
                        <button
                            className="sidebar-toggle d-xl-none"
                            onClick={toggleSidebar}
                            aria-label="Abrir el menú"
                            aria-expanded={isSidebarOpen}
                        >
                            <i className="fa-solid fa-bars" aria-hidden="true"></i>
                        </button>
                        <h4 className="page-title">{title}</h4>
                    </div>

                    <div className="topbar-actions">
                        {actions}
                        <NavLink to="/" className="topbar-link">
                            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
                            <span className="topbar-link-text">Ver sitio</span>
                        </NavLink>
                    </div>
                </header>

                <div className="cpanel-content">
                    <Container fluid className="p-0">
                        <Outlet />
                    </Container>
                </div>
            </main>

            {isSidebarOpen && (
                <div className="sidebar-backdrop d-xl-none" onClick={toggleSidebar} aria-hidden="true"></div>
            )}
        </div>
    );
}
